import { Loader2, Download } from "lucide-react";

const FORMATS = [
  { fmt: "STL",  baseHint: "3D printing · jewellery CAD (Rhino, Matrix, JewelCAD)" },
  { fmt: "OBJ",  baseHint: "Universal mesh · ZBrush, Blender" },
  { fmt: "FBX",  baseHint: "Animation / DCC pipelines · Maya, 3ds Max" },
  { fmt: "USDZ", baseHint: "Apple AR preview · iPhone / Vision Pro" },
  { fmt: "GLTF", baseHint: "Web-friendly JSON variant of GLB" },
  { fmt: "3MF",  baseHint: "Modern 3D printing format" },
];

/**
 * Split-button used inside the CAD dialog's job card.
 *
 *  ┌────────────── Download .glb ─────────────┬─ ▼ ─┐
 *  │  primary download (free, the active glb) │     │
 *  └──────────────────────────────────────────┴─────┘
 *           opens a popover with .stl / .obj / .fbx / …
 *           plus a Standard / Detailed quality toggle.
 *
 * The dropdown only matters when there's a ready mesh, so we keep all
 * the menu state inside the parent and only render it here based on
 * `menuOpen`. Each non-GLB format costs 1 credit.
 */
export default function MeshDownloadMenu({
  job,
  downloading,
  menuOpen,
  onToggleMenu,
  convertQuality,
  onChangeQuality,
  onDownloadFormat,
  insufficientCredits,
}) {
  return (
    <div className="relative mt-3" data-testid="mesh-download-group">
      <div className="flex items-stretch w-full rounded-full overflow-hidden shadow-[0_8px_30px_-8px_rgba(212,175,55,0.55)]">
        <button
          onClick={() => onDownloadFormat("GLB")}
          data-testid="cad-mesh-download-btn"
          disabled={downloading}
          className="flex-1 inline-flex items-center justify-center gap-2 bg-[#D4AF37] text-black text-sm font-medium py-3 hover:bg-[#F3CE5A] transition disabled:opacity-60"
        >
          {downloading ? <><Loader2 className="w-4 h-4 animate-spin" /> Downloading…</> : <><Download className="w-4 h-4" /> Download .glb {job.mesh_size_bytes ? `· ${(job.mesh_size_bytes / 1024).toFixed(0)} KB` : ""}</>}
        </button>
        <button
          type="button"
          onClick={onToggleMenu}
          disabled={downloading}
          data-testid="cad-mesh-format-toggle"
          aria-haspopup="menu"
          aria-expanded={menuOpen}
          title="Choose a different format"
          className="px-4 bg-[#B8941E] text-black hover:bg-[#D4AF37] transition border-l border-black/15 disabled:opacity-60"
        >
          <svg width="12" height="8" viewBox="0 0 12 8" fill="none" className={`transition-transform ${menuOpen ? "rotate-180" : ""}`}>
            <path d="M1 1.5L6 6.5L11 1.5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </button>
      </div>
      {menuOpen && (
        <div
          role="menu"
          data-testid="cad-mesh-format-menu"
          className="absolute right-0 left-0 mt-2 z-30 rounded-xl border border-white/10 bg-[#0A0A0A] shadow-2xl overflow-hidden"
        >
          <div className="px-3 py-2 text-[9px] uppercase tracking-[0.22em] text-white/45 border-b border-white/8">
            Other formats · 1 credit each
          </div>
          <div className="px-3 py-2 border-b border-white/8 flex items-center justify-between gap-2">
            <span className="text-[10px] uppercase tracking-[0.18em] text-white/55">Quality</span>
            <div className="inline-flex items-center gap-0.5 p-0.5 rounded-full bg-[#141414] border border-white/8">
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); onChangeQuality("standard"); }}
                data-testid="cad-mesh-quality-standard"
                className={`text-[10px] uppercase tracking-[0.16em] px-2.5 py-1 rounded-full transition ${convertQuality === "standard" ? "bg-[#D4AF37] text-black" : "text-white/55 hover:text-white"}`}
              >
                Standard
              </button>
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); onChangeQuality("detailed"); }}
                data-testid="cad-mesh-quality-detailed"
                title="Casting-grade density · ~200 k face limit. Bigger file, denser mesh."
                className={`text-[10px] uppercase tracking-[0.16em] px-2.5 py-1 rounded-full transition ${convertQuality === "detailed" ? "bg-[#D4AF37] text-black" : "text-white/55 hover:text-white"}`}
              >
                Detailed
              </button>
            </div>
          </div>
          {FORMATS.map((opt) => {
            const hint = opt.baseHint + (
              convertQuality === "detailed" && (opt.fmt === "STL" || opt.fmt === "OBJ")
                ? " · ~200 k faces" : ""
            );
            return (
              <button
                key={opt.fmt}
                type="button"
                role="menuitem"
                onClick={() => onDownloadFormat(opt.fmt)}
                disabled={downloading || insufficientCredits}
                data-testid={`cad-mesh-format-${opt.fmt.toLowerCase()}`}
                className="w-full text-left px-3 py-2.5 hover:bg-[#D4AF37]/10 transition flex items-center justify-between gap-3 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <div className="flex items-center gap-2">
                  <Download className="w-3.5 h-3.5 text-[#D4AF37]" />
                  <span className="text-sm text-white font-medium">.{opt.fmt.toLowerCase()}</span>
                </div>
                <span className="text-[10px] text-white/45 leading-tight text-right max-w-[60%]">{hint}</span>
              </button>
            );
          })}
          <div className="px-3 py-2 text-[10px] text-white/40 border-t border-white/8 leading-relaxed">
            Converts on demand · ~10 s · re-uses your existing 3D mesh.
          </div>
        </div>
      )}
    </div>
  );
}
